import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Error } from '@figure/frontlib-gui';
import { Typography } from 'shared/components';
import { usePersonalLoan, usePersonalLoanRouteFsm } from 'shared/hooks';
import IDUploadOptions from './IdUploadOptions';
import IDUploadForm from './IDUploadForm';
// Disabling email and bypass until s-a-p supports
// import AlreadyUploadedID from './AlreadyUploadedId';
import IDWebcamForm from './IDWebcamForm';
import WebcamIDUploadForm from './WebcamIDUploadForm';

const Copy = styled(Typography.Body)`
  margin-bottom: 16px;

  :last-child {
    margin-bottom: 32px;
  }
`;

const CopyContainer = styled.div``;

const IDUploadContainer = () => {
  const { uuid: personalLoanUuid } = useParams();
  const [typeSettings, setTypeSettings] = useState({ docType: '', uploadMethod: '' });
  const [uploadOption, setUploadOption] = useState('');
  const {
    personalLoan,
    uploadDocument,
    // Disabling email and bypass until s-a-p supports
    // bypassIDVerification,
    error: serverError,
  } = usePersonalLoan();
  const runRouter = usePersonalLoanRouteFsm();

  const onWebcamSubmit = async (data) => {
    await uploadDocument(data);
    runRouter();
  };

  const handleSubmit = async ({ file, type }) => {
    await uploadDocument({
      uuid: personalLoanUuid,
      type,
      file,
    });

    runRouter();
  };

  const resetForm = (docType) => {
    setTypeSettings((prevTypeSettings) => ({ ...prevTypeSettings, docType }));
  };

  const submitWebcam = (data) => {
    const type = `${typeSettings.docType}FRONT`;

    onWebcamSubmit({
      uuid: personalLoanUuid,
      file: data.blob,
      type,
    });
  };

  // Disabling email and bypass until s-a-p supports
  // const submitBypass = async () => {
  //   const triggerEmail = uploadOption === 'EMAIL';
  //   await bypassIDVerification({ personalLoanUuid, triggerEmail });
  //   onSubmit();
  // };

  // TODO: Clean up - function only gets called on change vs click
  const handleUploadOption = (value) => {
    if (value === uploadOption) setUploadOption('');
    else setUploadOption(value);
  };

  const handleWebcamUpload = () =>
    setTypeSettings((prevTypeSettings) => ({ ...prevTypeSettings, uploadMethod: 'WEBCAM' }));

  const isUpload = typeSettings.uploadMethod === 'UPLOAD';
  const isWebcam = typeSettings.uploadMethod && !isUpload;
  const webcamHeaderMap = {
    DRIVERSLICENSE: 'front of your valid US Driver’s License.',
    PERMANENTRESIDENT: 'front of your US Permanent Resident Card',
    IDCARD: 'front of your US State ID.',
    PASSPORT: 'first page of your US Passport.',
  };
  const headerText = isWebcam
    ? `Take a photo of the ${webcamHeaderMap[typeSettings.docType]}`
    : 'Now let’s quickly verify your identity.';
  const subText = isWebcam
    ? 'Use your device camera to get a picture of your ID. Make sure you have good lighting and all four corners of your ID are visible in the picture.'
    : '';
  const showIDUpload = uploadOption === 'UPLOAD';
  const showWebcamUpload = uploadOption === 'WEBCAM';
  // Disabling email and bypass until s-a-p supports
  // const showEmailUpload = uploadOption === 'EMAIL';
  // const showBypass = uploadOption === 'BYPASS';

  return (
    <>
      <Typography.PageHeader>{headerText}</Typography.PageHeader>
      <h3>{subText}</h3>
      {isWebcam ? (
        <IDWebcamForm handleSubmit={submitWebcam} serverError={serverError} />
      ) : (
        <>
          <CopyContainer>
            <Copy>
              Please select a method below to share a copy of your photo identification. Supported
              ID types include <strong>valid</strong> US Driver’s License, State ID, or Passport.
            </Copy>
            <Copy>
              Make sure to provide a clear picture of your ID, with good lighting, and a plain
              background to avoid processing delays for your application.
            </Copy>
          </CopyContainer>

          {/* // Disabling email and bypass until s-a-p supports */}
          {/* {personalLoan.status?.idBypass && <AlreadyUploadedID personalLoanUuid={personalLoanUuid} />} */}
          <IDUploadOptions
            handleClick={handleUploadOption}
            uploadOption={uploadOption}
            idBypass={personalLoan.status?.idBypass}
            // Disabling email and bypass until s-a-p supports
            showEmail={false}
            showBypass={false}
          />

          <Error display="block">{serverError}</Error>

          {showIDUpload && <IDUploadForm handleSubmit={handleSubmit} handleResetType={resetForm} />}
          {showWebcamUpload && (
            <WebcamIDUploadForm handleResetType={resetForm} handleSubmitForm={handleWebcamUpload} />
          )}
          {/* // Disabling email and bypass until s-a-p supports */}
          {/* {(showEmailUpload || showBypass) && <BypassUpload handleSubmitForm={submitBypass} uploadOption={uploadOption} />} */}
        </>
      )}
    </>
  );
};

export default IDUploadContainer;
